import React, { useEffect, useState, useMemo } from "react";
import { List, Form, Modal, Input, Alert, Row, Col, Space, Select } from "antd";
import {
  CloseOutlined,
  PlusOutlined,
  TruckFilled,
  SaveFilled,
} from "@ant-design/icons";
import propTypes from "prop-types";
import {
  defaultFactoryWoodExportTransactionDetail,
  FACTORY_CHOICES,
} from "../../constance/dataTypes";
import { useTranslation } from "react-i18next";
import useItemDetail from "../../hooks/useItemDetail";
import useItemListDetail from "../../hooks/useItemListDetail";
import { URL_BARCODE, URL_SCALE_LARGE } from "../../constance/urls";
import { Toast } from "../../utils/Toast";
import { ellipsisString, formatComma } from "../../utils/stringFormat";
import ConfirmModal from "../common/ConfirmModal";
import { PATCH } from "../../utils/HttpClient";
import SelectTruckTransactionModal from "../truck-transaction/SelectTruckTransactionModal";
import * as _ from "lodash";
import TSTable from "../common/TSTable";
import { BLUE_COLOR, GREEN, WHITE_COLOR } from "../../constance/color";
import moment from "moment";
import { HUMAN_DATETIME_FORMAT } from "../../constance/strings.js"; // adjust the import path if needed
import ColorButton from "../common/ColorButton.js";

function FactoryWoodExportDetailModal(props) {
  const { open, onClose, detail } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const woodExport = useItemDetail({
    enableReinitialize: true,
    url: URL_BARCODE.WOOD_EXPORT_TRANSACTION,
    id: detail,
    manual: true,
    onSuccess: (type) => {
      switch (type) {
        case "save":
          Toast.success(`${t("save")} ${t("success")}`);
          break;
        case "export":
          Toast.success(`${t("factory_wood_exports.export")} ${t("success")}`);
          onClose();
          break;
        default:
          Toast.success(`${t("success")}`);
          break;
      }
    },
  });

  const truckTransactionItems = useItemListDetail({
    url: URL_SCALE_LARGE.TRUCK_TRANSACTION,
    idList: form.getFieldValue("truck_transactions") || [],
    manual: true,
  });

  const columns = useMemo(
    () => [
      {
        title: t("barcodes.barcode_id"),
        dataIndex: "barcode_no",
      },
      {
        title: t("barcodes.amount"),
        dataIndex: "wood_size_amount_map_list",
        render: (__, original) => (
          <List>
            {original.wood_size_amount_map_list.map(
              (wood_size_amount_map, index) => (
                <List.Item key={wood_size_amount_map.wood_code || index}>
                  {wood_size_amount_map.wood_code} -{" "}
                  {formatComma(wood_size_amount_map.amount)} {t("units.wood")}
                </List.Item>
              )
            )}
            <List.Item>
              {t("datas.total")} -{" "}
              {formatComma(
                original.wood_size_amount_map_list.reduce(
                  (prev, cur) => prev + Number(cur.amount),
                  0
                )
              )}{" "}
              {t("units.wood")}
            </List.Item>
          </List>
        ),
      },
    ],
    [t]
  );

  const [truckTransactions, setTruckTransactions] = useState([]);
  const [isSelectTruckTransactionOpen, setIsSelectTruckTransactionOpen] =
    useState(false);
  const [isExportConfirmVisible, setIsExportConfirmVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState(null);

  const handleSave = async () => {
    setIsLoading(true);
    setErrorMessages(null);
    try {
      await Promise.all(
        truckTransactions.map(async (tt) => {
          return PATCH(`${URL_SCALE_LARGE.TRUCK_COMPANY}${tt.truck_company}/`, {
            address: tt.truck_company_address,
          });
        })
      );
      const formValues = await form.validateFields();
      const payload = {
        ...formValues,
        truck_transactions: truckTransactions.map((tt) => tt.id),
      };
      await PATCH(`${URL_BARCODE.WOOD_EXPORT_TRANSACTION}${detail}/`, payload);
      Toast.success(`${t("save")} ${t("success")}`);
    } catch (error) {
      setErrorMessages(
        error.message || error.errorMessages || t("error.general")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderTruckTransactions = (truckTransaction) => {
    const license_plate = truckTransaction.license_plate;
    const truck_company_name = _.get(
      truckTransaction,
      "truck_company_detail.name",
      ""
    );
    const in_date_time = new Date(truckTransaction.in_date_time).toLocaleString(
      "en-GB"
    );

    return (
      <Form.Item>
        <Row gutter={24} key={truckTransaction.id}>
          <Col span={16}>
            <Form.Item
              label={t("trucks.license_plate")}
              style={{ marginBottom: 0 }}
            >
              <Input
                readOnly
                value={`${license_plate} - ${truck_company_name} (${in_date_time})`}
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item label={t("trucks.address")}>
              <Space.Compact>
                <Input
                  value={truckTransaction.truck_company_address}
                  onChange={(e) => {
                    const updatedAddress = e.target.value;
                    setTruckTransactions(
                      truckTransactions.map((tt) =>
                        tt.id !== truckTransaction.id
                          ? tt
                          : {
                              ...tt,
                              truck_company_address: updatedAddress,
                            }
                      )
                    );
                  }}
                  style={{ width: "270px" }}
                />
                <ColorButton
                  icon={<CloseOutlined />}
                  danger
                  onClick={() => {
                    const updatedTruckTransactions = truckTransactions.filter(
                      (tt) => tt.id !== truckTransaction.id
                    );
                    setTruckTransactions(updatedTruckTransactions);
                    form.setFieldsValue({
                      truck_transactions: updatedTruckTransactions.map(
                        (tt) => tt.id
                      ),
                    });
                  }}
                />
              </Space.Compact>
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>
    );
  };

  useEffect(() => {
    form.resetFields();
    woodExport.clearErrorMessages();
    setTruckTransactions([]);
    if (open && detail) {
      woodExport.fetchData();
    }
  }, [open, detail]);

  useEffect(() => {
    if (woodExport.response) {
      form.setFieldsValue(woodExport.response);
      const createdDate = moment(woodExport.response.created).format(
        HUMAN_DATETIME_FORMAT
      );
      form.setFieldsValue({ created: createdDate });
    }
  }, [woodExport.response]);

  useEffect(() => {
    if (truckTransactionItems.response) {
      setTruckTransactions(
        truckTransactionItems.response
          .filter((item) => !item.errorMessages)
          .map((item) => item.data)
      );
    }
  }, [truckTransactionItems.response]);

  useEffect(() => {
    if (
      form.getFieldValue("truck_transactions")?.length > 0 &&
      truckTransactions.length === 0
    ) {
      truckTransactionItems.fetchData();
    }
  }, [form.getFieldValue("truck_transactions")]);

  useEffect(() => {
    if (truckTransactions.length > 0) {
      form.setFieldsValue({
        truck_transactions: truckTransactions.map((tt) => tt.id),
      });
    }
  }, [truckTransactions]);

  return (
    <div>
      <ConfirmModal
        open={isExportConfirmVisible}
        onClose={() => setIsExportConfirmVisible(false)}
        title={t("factory_wood_exports.export")}
        content={`${t("workflows.confirmation_content")}: [${t(
          "factory_wood_exports.export"
        )}]?`}
        onConfirm={() => {
          woodExport.handleAction("post", true, "export");
          setIsExportConfirmVisible(false);
        }}
      />

      <SelectTruckTransactionModal
        open={isSelectTruckTransactionOpen}
        onClose={() => setIsSelectTruckTransactionOpen(false)}
        onSelect={(tt) => {
          if (!truckTransactions.find((t) => t.id === tt.id)) {
            setTruckTransactions([...truckTransactions, tt]);
          }
        }}
        filterParams={{ transaction_type: "2", mapped: false }}
      />

      <Modal
        key={detail}
        centered
        title={t("factory_wood_exports.detail")}
        visible={open}
        onCancel={onClose}
        width={1000}
        footer={[
          !form.getFieldValue("is_exported") &&
            truckTransactions.length > 0 && (
              <Space style={{ float: "left" }}>
                <ColorButton
                  key="export"
                  type="primary"
                  override={GREEN}
                  icon={<TruckFilled />}
                  loading={woodExport.isLoading || isLoading}
                  onClick={() => setIsExportConfirmVisible(true)}
                >
                  {t("factory_wood_exports.export")}
                </ColorButton>
              </Space>
            ),
          <ColorButton
            key="cancel"
            variant="filled"
            color="default"
            icon={<CloseOutlined />}
            loading={woodExport.isLoading || isLoading}
            onClick={onClose}
          >
            {t("cancel")}
          </ColorButton>,
          !form.getFieldValue("is_exported") && (
            <ColorButton
              htmlType="submit"
              type="primary"
              override={BLUE_COLOR}
              icon={<SaveFilled />}
              loading={woodExport.isLoading || isLoading}
              onClick={handleSave}
            >
              {t("save")}
            </ColorButton>
          ),
        ]}
      >
        {(woodExport.errorMessages || errorMessages) && (
          <Alert
            message={t("error")}
            description={
              <>
                <p>{woodExport.errorMessages}</p>
                <p>{errorMessages}</p>
              </>
            }
            type="error"
            showIcon
          />
        )}

        <Form
          layout="vertical"
          form={form}
          initialValues={defaultFactoryWoodExportTransactionDetail}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label={t("date")} name="created">
                <Input readOnly variant="borderless" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={t("factory_wood_exports.order_no")}
                name="order_no"
              >
                <Input readOnly variant="borderless" />
              </Form.Item>
            </Col>
            <Col span={8}>
              {form.getFieldValue("is_exported") ? (
                <Form.Item
                  label={t("factory_wood_exports.destination_factory")}
                  name="destination_factory"
                >
                  <Input readOnly variant="borderless" />
                </Form.Item>
              ) : (
                <Form.Item
                  label={t("factory_wood_exports.destination_factory")}
                  name="destination_factory"
                >
                  <Select
                    options={FACTORY_CHOICES.map((choice) => ({
                      label: choice.text,
                      value: choice.value,
                    }))}
                    onChange={(value) =>
                      form.setFieldsValue({ destination_factory: value })
                    }
                    placeholder={t("select_factory")}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
          {!form.getFieldValue("is_exported") && (
            <Form.Item name="add_truck_transaction">
              <ColorButton
                variant="outlined"
                color="primary"
                override={BLUE_COLOR}
                icon={<PlusOutlined />}
                onClick={() => setIsSelectTruckTransactionOpen(true)}
              >
                {t("factory_wood_exports.add_truck_transaction")}
              </ColorButton>
            </Form.Item>
          )}

          {truckTransactions.map((ttItem) => renderTruckTransactions(ttItem))}

          <Form.Item
            className="exportItemList-table"
            label={t("factory_wood_exports.export_products")}
          >
            <TSTable
              scroll={{ x: 1000 }}
              url={`${URL_BARCODE.WOOD_EXPORT}${detail}/`}
              columns={columns}
              showPagination
              extraFilterParams={{ page_size: 5 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

FactoryWoodExportDetailModal.defaultProps = {
  open: false,
  onClose: () => null,
  detail: defaultFactoryWoodExportTransactionDetail,
};

FactoryWoodExportDetailModal.propTypes = {
  open: propTypes.bool,
  onClose: propTypes.func,
  detail: propTypes.object,
};

export default React.memo(FactoryWoodExportDetailModal);
